import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { KitchenCard } from "../components/KitchenCard";
import { ExploreCarcass } from "../components/ExploreCarcass";
import { QualityPillars } from "../components/QualityPillars";
import { Journey } from "../components/Journey";
import { CTABand } from "../components/CTABand";
import { Counter } from "../components/Counter";
import { useEnquiry } from "../components/EnquiryModal";
import { IMGS } from "../lib/img";
import { SITE } from "../lib/site";
import {
  TRUST_STRIP,
  MARQUEE_ITEMS,
  CONSTRUCTION_POINTS,
  PROCESS_STEPS,
  STATS,
} from "../data/content";
import { KITCHENS } from "../data/kitchens";

const ease = [0.22, 1, 0.36, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-royaldeep"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          initial={{ scale: 1.14 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease }}
          src={IMGS.hero}
          alt="Premium modular kitchen by Paramount Kitchen, Kangra — engineered carcass with premium finishes"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-royaldeep/90 via-royaldeep/50 to-royaldeep/10" />
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-royaldeep/85 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-32 pt-44">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="eyebrow flex items-center gap-3 text-gold"
        >
          <span className="h-px w-10 bg-gold" /> Paramount Kitchen — Kangra, Himachal Pradesh
        </motion.p>

        <h1 className="mt-7 font-display text-5xl font-medium leading-[1.04] text-ivory sm:text-6xl lg:text-7xl">
          {["Beautiful by Design.", "Engineered to Last."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.4 + i * 0.16, duration: 1.05, ease }}
              >
                {i === 0 ? (
                  line
                ) : (
                  <>
                    <span className="serif-accent text-gold">Engineered</span> to Last.
                  </>
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg"
        >
          {SITE.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <Link
            to="/kitchens"
            data-testid="hero-explore-kitchens"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-royal shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddim hover:text-ivory"
          >
            Explore Kitchens
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/carcass"
            data-testid="hero-discover-carcass"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-8 py-4 text-sm font-semibold tracking-wide text-ivory backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
          >
            Discover Our Carcass
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-ivory/50"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.div>
    </section>
  );
};

const TrustStrip = () => (
  <section data-testid="trust-strip" className="border-b border-ink/10 bg-ivory">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-8 px-6 py-12 sm:grid-cols-3 lg:grid-cols-5">
      {TRUST_STRIP.map((t, i) => (
        <Reveal key={t.label} delay={i * 0.07}>
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-gold/40 bg-sand text-wood">
              <t.icon className="h-5 w-5" />
            </span>
            <span className="text-xs font-bold uppercase leading-snug tracking-[0.16em] text-ink/80">
              {t.label}
            </span>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const BeyondFinish = () => (
  <section data-testid="beyond-finish" className="bg-ivory py-24 md:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Reveal>
          <p className="eyebrow text-golddim">Brand Differentiation</p>
          <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-6xl">
            Look Beyond the <span className="serif-accent text-golddim">Finish.</span>
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/70">
            A kitchen may look beautiful on the outside, but its real strength lies beneath the
            surface. That is why we focus on the complete construction of the kitchen — not only
            the finish you see on day one.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          {CONSTRUCTION_POINTS.map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <div className="flex items-center gap-3 border-b border-ink/8 py-3">
                <span className="h-2 w-2 shrink-0 rotate-45 bg-gold" />
                <span className="text-sm font-medium text-ink/80">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/carcass"
            data-testid="explore-our-construction"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 text-sm font-semibold text-ivory shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-royaldeep"
          >
            Explore Our Construction
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="lg:col-span-5">
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full rounded-3xl border-2 border-gold/50" />
            <img
              src={IMGS.plywoodStack}
              alt="Stacked plywood sheets ready for engineered kitchen manufacturing at Paramount Kitchen, Kangra"
              loading="lazy"
              className="relative aspect-[4/5] w-full rounded-3xl object-cover shadow-xl"
            />
            <div className="absolute -bottom-8 -left-6 hidden w-56 rounded-2xl border border-ink/10 bg-white p-4 shadow-xl sm:block">
              <p className="eyebrow text-golddim">Panel Cross-Section</p>
              <div className="mt-3 space-y-1">
                {["Laminate", "Adhesive", "Plywood", "Adhesive", "Laminate"].map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span
                      className={`h-2.5 flex-1 rounded-sm ${
                        l === "Plywood"
                          ? "bg-wood ply-texture"
                          : l === "Adhesive"
                          ? "bg-amber-300"
                          : "bg-gold"
                      }`}
                    />
                    <span className="w-16 text-[9px] font-bold uppercase tracking-wider text-ink/60">
                      {l}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Featured = () => (
  <section className="bg-sand py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow text-golddim">The Collection</p>
          <h2 className="text-balance mt-5 max-w-xl font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
            Kitchens for every <span className="serif-accent text-golddim">Indian home.</span>
          </h2>
        </div>
        <Link
          to="/kitchens"
          data-testid="view-all-kitchens"
          className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-ink hover:text-ivory"
        >
          View All 20 Kitchens
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {KITCHENS.slice(0, 6).map((k, i) => (
          <Reveal key={k.slug} delay={(i % 3) * 0.08}>
            <KitchenCard kitchen={k} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProcessPreview = () => (
  <section className="bg-ivory py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal>
        <p className="eyebrow text-golddim">Manufacturing</p>
        <h2 className="text-balance mt-5 max-w-2xl font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
          Ten steps between <span className="serif-accent text-golddim">plywood and kitchen.</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {PROCESS_STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <Link
              to="/process"
              data-testid={`process-preview-step-${s.n}`}
              className="group block h-full rounded-2xl border border-ink/10 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
            >
              <span className="font-display text-3xl font-semibold text-gold/50 transition-colors group-hover:text-gold">
                {s.n}
              </span>
              <p className="mt-3 text-sm font-semibold leading-snug text-ink">{s.name}</p>
            </Link>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 grid grid-cols-2 gap-8 rounded-3xl border border-ink/10 bg-white p-8 sm:grid-cols-4 md:p-12">
        {STATS.map((s) => (
          <div key={s.label} className="text-center">
            <p className="font-display text-5xl font-medium text-royal md:text-6xl">
              <Counter to={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink/55">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Quality = () => (
  <section className="bg-parch py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6">
      <Reveal className="max-w-2xl">
        <p className="eyebrow text-golddim">Our Standard</p>
        <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
          Made with attention to <span className="serif-accent text-golddim">what you don&rsquo;t see.</span>
        </h2>
      </Reveal>
      <div className="mt-14">
        <QualityPillars />
      </div>
    </div>
  </section>
);

const JourneySection = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="bg-ivory py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow text-golddim">Customer Journey</p>
          <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
            From first call to <span className="serif-accent text-golddim">first meal.</span>
          </h2>
        </Reveal>
        <div className="mt-14">
          <Journey />
        </div>
        <Reveal className="mt-12 text-center">
          <button
            data-testid="start-your-kitchen-journey"
            onClick={() => openEnquiry({ source: "journey" })}
            className="inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 text-sm font-semibold text-ivory shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-royaldeep"
          >
            Start Your Kitchen Journey <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <SEO
        title="Paramount Kitchen | Premium Modular Kitchens in Kangra, Himachal Pradesh"
        description="Premium modular kitchens crafted with precision, carefully selected materials and engineered carcass construction. Beautiful by design, engineered to last — Kangra District, Himachal Pradesh."
      />
      <Hero />
      <TrustStrip />
      <Marquee items={MARQUEE_ITEMS} />
      <BeyondFinish />
      <Featured />
      <ExploreCarcass />
      <ProcessPreview />
      <Quality />
      <JourneySection />
      <CTABand />
    </>
  );
}
